/* Phase D + E — Admin/Manager surfaces:
 *   - IntegrationHealthTab: browse outbound/inbound events, retry failed
 *   - LeadBulkImportTab: CSV / Excel upload with preview + import
 *   - SaPresenceWidget: real-time SA presence grid
 */
import { useEffect, useRef, useState } from "react";
import { AlertCircle, CheckCircle2, Download, FileUp, Loader2, RefreshCw, Users } from "lucide-react";
import { http, notify } from "../api";
import { Loading } from "./common";


/* ================================================================== */
/* INTEGRATION HEALTH & RETRY                                          */
/* ================================================================== */
export function IntegrationHealthTab() {
  const [filters, setFilters] = useState({ direction: "outbound", status: "", event: "", days: 7 });
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadHealth = () => http.get("/integrations/calling-app/health").then((r) => setHealth(r.data)).catch(() => {});
  const load = () => {
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null) q.set(k, v); });
    q.set("limit", "150");
    http.get(`/admin/integration/events?${q.toString()}`)
      .then((r) => setData(r.data))
      .catch((err) => notify.error(err, "Could not load integration events"));
  };
  useEffect(() => { loadHealth(); load(); /* eslint-disable-next-line */ }, []);

  const retry = async (id) => {
    try {
      const r = await http.post(`/admin/integration/events/${id}/retry`);
      notify.success(`Retry: ${r.data.delivery.status}${r.data.delivery.error ? ` — ${r.data.delivery.error}` : ""}`);
      load();  // refresh the events table so the new retry row shows up
    } catch (err) { notify.error(err, "Retry failed"); }
  };

  const retryAll = async () => {
    if (!window.confirm(`Retry ALL failed outbound webhooks in the last ${filters.days} days?`)) return;
    setBusy(true);
    try {
      const r = await http.post(`/admin/integration/retry-failed?days=${filters.days}`);
      notify.success(`Retried ${r.data.attempted} events: ${r.data.ok} ok, ${r.data.error} still failing`);
      load();
    } catch (err) { notify.error(err, "Bulk retry failed"); }
    finally { setBusy(false); }
  };

  if (data === null) return <Loading>Loading integration health…</Loading>;

  return (
    <section className="panel">
      <div className="section-title">
        <h3>Integration health · Calling App</h3>
        <button className="outline-button" onClick={() => { loadHealth(); load(); }}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {health && (
        <div className="health-grid" style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", marginBottom: 14 }}>
          <HealthChip label="Auth key" ok={health.auth_configured} />
          <HealthChip label="Webhook URL" ok={health.outbound_configured} />
          <HealthChip label="Signing secret" ok={health.signing_configured} />
          <HealthChip label="Server time" ok text={health.server_time?.slice(0, 19).replace("T", " ")} />
        </div>
      )}

      <div className="filter-bar" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <select data-testid="integ-direction-select" value={filters.direction} onChange={(e) => setFilters({ ...filters, direction: e.target.value })}>
          <option value="">Both directions</option>
          <option value="outbound">Outbound (CRM → Calling App)</option>
          <option value="inbound">Inbound (Calling App → CRM)</option>
        </select>
        <select data-testid="integ-status-select" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All statuses</option>
          <option value="ok">Delivered (ok)</option>
          <option value="error">Failed (error)</option>
          <option value="skipped">Skipped</option>
        </select>
        <input
          data-testid="integ-event-input"
          placeholder="Event name filter (e.g. application.disbursed)"
          value={filters.event} onChange={(e) => setFilters({ ...filters, event: e.target.value })}
          style={{ minWidth: 260 }}
        />
        <select data-testid="integ-days-select" value={filters.days} onChange={(e) => setFilters({ ...filters, days: Number(e.target.value) })}>
          <option value={1}>Last 24h</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
        </select>
        <button className="primary-button" onClick={load} data-testid="integ-search-button">Apply</button>
        <span style={{ flex: 1 }} />
        <button className="outline-button danger-button" onClick={retryAll} disabled={busy} data-testid="integ-retry-all-button">
          {busy ? <Loader2 size={13} className="spin" /> : <RefreshCw size={13} />} Retry all failed
        </button>
      </div>

      <div className="counts-row" style={{ display: "flex", gap: 16, marginBottom: 10 }}>
        <span className="muted">Total: <strong>{data.counts.total}</strong></span>
        <span className="muted">OK: <strong style={{ color: "#3aa06c" }}>{data.counts.ok}</strong></span>
        <span className="muted">Failed: <strong style={{ color: "#d0453f" }}>{data.counts.error}</strong></span>
        <span className="muted">Skipped: <strong>{data.counts.skipped}</strong></span>
      </div>

      {data.events.length === 0 ? (
        <div className="empty">No events match those filters in the last {data.days} days.</div>
      ) : (
        <table className="records-table">
          <thead>
            <tr>
              <th>When</th><th>Event</th><th>Direction</th><th>Lead / SA</th><th>Status</th><th>Error</th><th></th>
            </tr>
          </thead>
          <tbody>
            {data.events.map((e) => (
              <tr key={e.id} data-testid={`integ-event-row-${e.id}`}>
                <td className="muted" style={{ whiteSpace: "nowrap" }}>{(e.created_at || "").slice(0, 19).replace("T", " ")}</td>
                <td><strong>{e.event}</strong></td>
                <td>{e.direction}</td>
                <td className="muted">
                  {e.lead_id || (e.payload && e.payload.employee_id) || "—"}
                </td>
                <td>
                  <span className={`chip chip-${e.status}`}>{e.status}</span>
                </td>
                <td className="muted" style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis" }} title={e.error}>
                  {e.error || ""}
                </td>
                <td>
                  {e.direction === "outbound" && e.status !== "ok" && (
                    <button className="icon-button" onClick={() => retry(e.id)} data-testid={`integ-retry-${e.id}`} title="Retry">
                      <RefreshCw size={13} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function HealthChip({ label, ok, text }) {
  return (
    <div style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid #e6e6e6" }}>
      <p className="eyebrow" style={{ margin: 0 }}>{label}</p>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
        {ok ? <CheckCircle2 size={14} color="#3aa06c" /> : <AlertCircle size={14} color="#d0453f" />}
        <strong>{text || (ok ? "Ready" : "Not set")}</strong>
      </div>
    </div>
  );
}


/* ================================================================== */
/* LEAD BULK IMPORT                                                     */
/* ================================================================== */
export function LeadBulkImportTab() {
  const fileInput = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [skipDup, setSkipDup] = useState(true);
  const [result, setResult] = useState(null);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setPreview(null);
    setResult(null);
  };

  const doPreview = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await http.post("/admin/leads/bulk-preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
    } catch (err) { notify.error(err, "Preview failed"); }
    finally { setBusy(false); }
  };

  const doImport = async () => {
    if (!file) return;
    if (!window.confirm(`Import ${preview?.total_rows || "these"} rows now?`)) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await http.post(
        `/admin/leads/bulk-import?skip_duplicates=${skipDup}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setResult(r.data);
      notify.success(`Import done — ${r.data.created} new, ${r.data.updated} updated, ${r.data.skipped} skipped, ${r.data.errors} errored`);
    } catch (err) { notify.error(err, "Import failed"); }
    finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await http.get("/admin/leads/bulk-template");
      const cols = r.data.columns.join(",");
      const blob = new Blob([cols + "\n"], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "rupee-xpert-leads-template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { notify.error(err, "Could not fetch template"); }
  };

  return (
    <section className="panel">
      <div className="section-title">
        <h3>Bulk import leads</h3>
        <button className="outline-button" onClick={downloadTemplate} data-testid="lead-import-template-button">
          <Download size={13} /> Download CSV template
        </button>
      </div>

      <p className="muted">
        Upload a CSV or Excel (.xlsx / .xls) file with your leads. Duplicates are matched on <code>external_id</code>{" "}
        (preferred) or <code>mobile</code>. Existing production data is never deleted.
      </p>

      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", margin: "10px 0" }}>
        <input
          ref={fileInput}
          type="file"
          accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          onChange={onPick}
          data-testid="lead-import-file-input"
        />
        <button className="outline-button" disabled={!file || busy} onClick={doPreview} data-testid="lead-import-preview-button">
          {busy ? "Reading…" : "Preview"}
        </button>
        <label className="muted" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={skipDup} onChange={(e) => setSkipDup(e.target.checked)} data-testid="lead-import-skip-dup-checkbox" />
          Skip duplicates
        </label>
        <button
          className="primary-button"
          disabled={!preview || busy}
          onClick={doImport}
          data-testid="lead-import-run-button"
        >
          <FileUp size={13} /> Import {preview ? `${preview.total_rows} rows` : ""}
        </button>
      </div>

      {preview && (
        <div className="preview-block" style={{ marginTop: 10 }}>
          <p className="eyebrow">PREVIEW</p>
          <p>
            <strong>{preview.total_rows}</strong> rows detected · <strong>{preview.duplicates}</strong> duplicates
            {preview.warnings?.length > 0 && ` · ${preview.warnings.length} warnings`}
          </p>
          {preview.warnings?.length > 0 && (
            <ul className="warning-list" style={{ maxHeight: 120, overflow: "auto", fontSize: 13, color: "#a06400" }}>
              {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          )}
          <div style={{ overflowX: "auto", maxHeight: 340 }}>
            <table className="records-table">
              <thead>
                <tr>
                  {Object.keys(preview.sample[0] || {}).map((c) => <th key={c}>{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {preview.sample.map((r, i) => (
                  <tr key={i}>
                    {Object.keys(preview.sample[0] || {}).map((c) => (
                      <td key={c} className="muted" style={{ whiteSpace: "nowrap" }}>{String(r[c] ?? "")}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {result && (
        <div style={{ marginTop: 14 }} data-testid="lead-import-result">
          <p className="eyebrow">RESULT</p>
          <ul>
            <li><strong>{result.created}</strong> new leads created</li>
            <li><strong>{result.updated}</strong> existing leads updated</li>
            <li><strong>{result.skipped}</strong> duplicates skipped</li>
            <li><strong>{result.errors}</strong> rows errored</li>
          </ul>
          {result.error_rows?.length > 0 && (
            <details><summary>Error rows ({result.error_rows.length})</summary>
              <ul style={{ fontSize: 13 }}>
                {result.error_rows.map((r, i) => <li key={i}>Row {r.row}: {r.error}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}
    </section>
  );
}


/* ================================================================== */
/* SA PRESENCE — Real-time widget                                       */
/* ================================================================== */
export function SaPresenceWidget({ pollMs = 20000, compact = false }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => http.get("/reports/sa-presence")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => {});
    load();
    const t = setInterval(load, pollMs);
    return () => { alive = false; clearInterval(t); };
  }, [pollMs]);

  if (!data) return <div className="panel"><Loading>Loading SA presence…</Loading></div>;
  const rows = compact ? data.sales_associates.slice(0, 8) : data.sales_associates;

  return (
    <section className="panel" data-testid="sa-presence-panel">
      <div className="section-title">
        <h3><Users size={16} /> Sales Associates · Live</h3>
        <span className="muted" style={{ fontSize: 12 }}>Updated {(data.generated_at || "").slice(11, 19)}</span>
      </div>
      <div className="presence-summary" style={{ display: "flex", gap: 12, marginBottom: 10 }}>
        <Pill color="#3aa06c">{data.summary.online} online</Pill>
        <Pill color="#c86f00">{data.summary.on_call} on call</Pill>
        <Pill color="#7b7b7b">{data.summary.away} away</Pill>
        <Pill color="#b3b3b3">{data.summary.offline} offline</Pill>
      </div>
      {rows.length === 0 ? (
        <div className="empty">No approved Sales Associates yet.</div>
      ) : (
        <div className="presence-grid" style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}>
          {rows.map((sa) => (
            <div key={sa.employee_id} className={`presence-card status-${sa.status}`}
                 data-testid={`sa-presence-card-${sa.employee_id}`}
                 style={{ display: "flex", gap: 10, padding: 10, border: "1px solid #eee", borderRadius: 10 }}>
              {sa.photo_url ? (
                <img src={sa.photo_url} alt={sa.name} style={{ width: 40, height: 40, borderRadius: 20, objectFit: "cover" }} />
              ) : (
                <div style={{ width: 40, height: 40, borderRadius: 20, background: "#eee", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {(sa.name || "?").slice(0, 2).toUpperCase()}
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {sa.name}
                </strong>
                <small className="muted">{sa.team_name}</small>
                <div style={{ marginTop: 2 }}><span className={`chip chip-${sa.status}`}>{sa.status}</span></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Pill({ color, children }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 999, background: color + "22", color, fontSize: 12, fontWeight: 600 }}>
      <span style={{ width: 8, height: 8, borderRadius: 4, background: color }} />
      {children}
    </span>
  );
}



/* ================================================================== */
/* CAMPAIGN → CALLING APP ASSIGNMENT SYNC DIAGNOSTIC                    */
/* ================================================================== */
export function CampaignSyncDiagnosticTab() {
  const [form, setForm] = useState({ campaign: "Data Bajaj BT", team: "SAIMA", sa: "AA", days: 30 });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await http.post("/admin/diagnostics/campaign-assignment-sync", form);
      setResult(r.data);
    } catch (err) {
      notify.error(err, "Diagnostic failed");
    } finally {
      setBusy(false);
    }
  };

  const codeColor = { A: "#f59e0b", B: "#dc2626", C: "#dc2626", D: "#dc2626" };

  return (
    <section className="panel" data-testid="admin-campaign-sync-diagnostic">
      <div className="section-title">
        <h3>Campaign → Calling App assignment sync diagnostic</h3>
        <span className="muted">Read-only · Admin only · No secrets displayed</span>
      </div>

      <div style={{ display: "grid", gap: 8, gridTemplateColumns: "1fr 1fr 1fr 100px auto", alignItems: "end", marginTop: 12 }}>
        <label>
          <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Campaign (name or id)</div>
          <input value={form.campaign} onChange={(e) => setForm({ ...form, campaign: e.target.value })} data-testid="diag-campaign-input" />
        </label>
        <label>
          <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Team (optional)</div>
          <input value={form.team} onChange={(e) => setForm({ ...form, team: e.target.value })} data-testid="diag-team-input" />
        </label>
        <label>
          <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Sales Associate (name / email / id)</div>
          <input value={form.sa} onChange={(e) => setForm({ ...form, sa: e.target.value })} data-testid="diag-sa-input" />
        </label>
        <label>
          <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Look back (days)</div>
          <input type="number" min="1" max="90" value={form.days} onChange={(e) => setForm({ ...form, days: Number(e.target.value) || 30 })} data-testid="diag-days-input" />
        </label>
        <button className="primary-button" onClick={run} disabled={busy || !form.campaign || !form.sa} data-testid="diag-run-btn">
          {busy ? <><Loader2 size={13} className="spin" /> Diagnosing…</> : "Run diagnostic"}
        </button>
      </div>

      {result && result.resolution_error && (
        <div className="panel" style={{ marginTop: 16, borderColor: "#dc2626" }} data-testid="diag-resolution-error">
          <AlertCircle size={16} style={{ color: "#dc2626" }} /> <b>{result.resolution_error}</b>
        </div>
      )}

      {result && !result.resolution_error && (
        <>
          <div style={{ marginTop: 16, padding: 16, borderRadius: 8, background: (codeColor[result.root_cause?.code] || "#94a3b8") + "18", border: "1px solid " + (codeColor[result.root_cause?.code] || "#94a3b8") }} data-testid="diag-root-cause">
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <span style={{ padding: "2px 10px", borderRadius: 999, background: codeColor[result.root_cause?.code] || "#94a3b8", color: "#fff", fontSize: 12, fontWeight: 700 }}>Root cause: {result.root_cause?.code}</span>
              <b>{result.root_cause?.summary}</b>
            </div>
            <div className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>{result.root_cause?.details}</div>
          </div>

          <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr", marginTop: 16 }}>
            <div className="panel">
              <b>Campaign</b>
              <div style={{ fontSize: 13, marginTop: 6 }}>
                <div><span className="muted">campaign_id:</span> <code>{result.campaign?.campaign_id}</code></div>
                <div><span className="muted">name:</span> {result.campaign?.name}</div>
                <div><span className="muted">status:</span> {result.campaign?.status}</div>
                <div><span className="muted">team:</span> {result.campaign?.team_name} <code>{result.campaign?.team_id}</code></div>
                <div><span className="muted">leads_per_sa:</span> {result.campaign?.leads_per_sa || "—"}</div>
              </div>
            </div>

            <div className="panel">
              <b>Sales Associate</b>
              <div style={{ fontSize: 13, marginTop: 6 }}>
                <div><span className="muted">name:</span> {result.sa?.name}</div>
                <div><span className="muted">email:</span> {result.sa?.email}</div>
                <div><span className="muted">CRM employee_id:</span> <code>{result.sa?.employee_id}</code></div>
                <div><span className="muted">role:</span> {result.sa?.role} · <span className="muted">approval:</span> {result.sa?.approval_status || "—"} · <span className="muted">active:</span> {String(result.sa?.active)}</div>
                <div><span className="muted">team_id:</span> <code>{result.sa?.team_id || "—"}</code></div>
              </div>
            </div>

            <div className="panel">
              <b>Lead counts</b>
              <div style={{ fontSize: 13, marginTop: 6 }}>
                <div><span className="muted">Campaign total leads:</span> {result.counts?.campaign_total_leads}</div>
                <div><span className="muted">Leads in team on this campaign:</span> {result.counts?.leads_in_team}</div>
                <div><span className="muted">Leads on this campaign assigned to SA:</span> <b>{result.counts?.leads_assigned_to_sa}</b></div>
                <div><span className="muted">campaign_leads rows in status=assigned to SA:</span> {result.counts?.campaign_leads_assigned_to_sa}</div>
                <div style={{ marginTop: 6 }}><span className="muted">Sample lead assigned to SA:</span> <code>{result.sample_lead?.lead_id || "—"}</code></div>
              </div>
            </div>

            <div className="panel">
              <b>Webhook config</b>
              <div style={{ fontSize: 13, marginTop: 6 }}>
                <div><span className="muted">URL host:</span> <code>{result.webhook_config?.host || "(not set)"}</code></div>
                <div><span className="muted">URL path:</span> <code>{result.webhook_config?.path || "(not set)"}</code></div>
                <div><span className="muted">URL configured:</span> {result.webhook_config?.url_configured ? <CheckCircle2 size={13} style={{ color: "#16a34a", verticalAlign: "middle" }} /> : <AlertCircle size={13} style={{ color: "#dc2626", verticalAlign: "middle" }} />}</div>
                <div><span className="muted">Secret configured:</span> {result.webhook_config?.secret_configured ? <CheckCircle2 size={13} style={{ color: "#16a34a", verticalAlign: "middle" }} /> : <AlertCircle size={13} style={{ color: "#dc2626", verticalAlign: "middle" }} />}</div>
                <div><span className="muted">Api key configured:</span> {result.webhook_config?.api_key_configured ? <CheckCircle2 size={13} style={{ color: "#16a34a", verticalAlign: "middle" }} /> : <AlertCircle size={13} style={{ color: "#dc2626", verticalAlign: "middle" }} />}</div>
              </div>
            </div>

            <div className="panel" style={{ gridColumn: "1 / -1" }}>
              <b>lead.assignment_changed delivery for this SA · last {result.input?.days} days</b>
              <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(4, 1fr)", marginTop: 8, fontSize: 13 }}>
                <div><span className="muted">Events generated:</span> <b>{result.delivery?.events_generated_for_sa}</b></div>
                <div><span className="muted">Delivered ok (2xx):</span> <b style={{ color: "#16a34a" }}>{result.delivery?.ok}</b></div>
                <div><span className="muted">Failed:</span> <b style={{ color: "#dc2626" }}>{result.delivery?.error}</b></div>
                <div><span className="muted">Skipped:</span> <b>{result.delivery?.skipped}</b></div>
              </div>
              {result.delivery?.http_status_codes && Object.keys(result.delivery.http_status_codes).length > 0 && (
                <div style={{ marginTop: 8, fontSize: 13 }}>
                  <span className="muted">HTTP status distribution:</span>{" "}
                  {Object.entries(result.delivery.http_status_codes).map(([k, v]) => (
                    <code key={k} style={{ marginRight: 8 }}>{k}={v}</code>
                  ))}
                </div>
              )}
              {result.delivery?.sample_errors?.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 13 }}>
                  <span className="muted">Sample error messages:</span>
                  <ul style={{ margin: "4px 0 0 20px", padding: 0 }}>
                    {result.delivery.sample_errors.map((e, i) => (<li key={i}><code>{e}</code></li>))}
                  </ul>
                </div>
              )}
              {result.delivery?.last_event_at && (
                <div style={{ marginTop: 8, fontSize: 12 }} className="muted">Most recent event: {result.delivery.last_event_at}</div>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
}
